import type { PlanSuggestion as PlanSuggestionType } from "@runlens/shared";
import {
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  PlusIcon,
  MinusIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";

interface PlanSuggestionProps {
  suggestion: PlanSuggestionType;
  onAccept: () => void;
  onDecline: () => void;
  isLoading?: boolean;
}

const actionIcons = {
  add: PlusIcon,
  update: PencilIcon,
  delete: MinusIcon,
};

const actionLabels = {
  add: "Add",
  update: "Update",
  delete: "Remove",
};

const actionColors = {
  add: "text-green-600 bg-green-50",
  update: "text-blue-600 bg-blue-50",
  delete: "text-red-600 bg-red-50",
};

function formatDate(dateStr: string): string {
  // Parse date parts to avoid timezone issues with date-only strings
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function PlanSuggestion({
  suggestion,
  onAccept,
  onDecline,
  isLoading,
}: PlanSuggestionProps) {
  return (
    <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-brand-100 rounded-full shrink-0">
          <ArrowPathIcon className="w-5 h-5 text-brand-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900">
            Suggested Plan Update
          </h4>
          <p className="mt-1 text-sm text-gray-600">{suggestion.explanation}</p>

          {/* Changes list */}
          <div className="mt-3 space-y-2">
            {suggestion.changes.map((change, index) => {
              const Icon = actionIcons[change.action];
              const label = actionLabels[change.action];
              const colorClass = actionColors[change.action];

              return (
                <div
                  key={index}
                  className="flex items-center gap-2 text-sm"
                >
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
                  >
                    <Icon className="w-3 h-3" />
                    {label}
                  </span>
                  <span className="text-gray-600">
                    {formatDate(change.date)}
                    {change.session && (
                      <>
                        {" · "}
                        <span className="font-medium">
                          {change.session.sessionType}
                        </span>
                        {change.session.targetDuration && (
                          <> · {change.session.targetDuration}min</>
                        )}
                        {change.session.targetDistance && (
                          <> · {(change.session.targetDistance / 1000).toFixed(1)}km</>
                        )}
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={onAccept}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <CheckIcon className="w-4 h-4" />
              Accept Changes
            </button>
            <button
              type="button"
              onClick={onDecline}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
